import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Table,
  Form,
  OverlayTrigger,
  Tooltip,
  Dropdown,
  Button,
  Spinner,
} from "react-bootstrap";
import axios from "axios";
import { useSearchParams } from "react-router-dom";

import { FaSearch } from "react-icons/fa";
import { ImCross } from "react-icons/im";
import { AiOutlineCheck } from "react-icons/ai";

import {
  onSearchMappings,
  onDeleteSelectedMappings,
  onSelectAllMappings,
  onFilterSelectedMappings,
  validateHubspotProperties,
  checkUnMappedTripletexFields,
  onSaveEditedMappings,
  onSelectMapping,
  onCancelMapping,
  onEditMapping,
  onDeleteExistingMapping,
  checkIsMappingsSaved,
} from "../../store/slices/mappingSlice";
import {
  setShowErrorModal,
  setShowSuccessModal,
} from "../../store/slices/preferenceSlice";
import { getUrl } from "../../helpers/getUrl";

import SearchResultsNotFound from "./SearchResultsNotFound";
import MappingDropdown from "./MappingDropdown";
import Pagination from "./Pagination";
import CustomProductMappingDropdown from "./CustomProductMappingDropdown";
import SideBar from "./Sidebar";

function RenderMappings({
  mapping,
  hubspotDropdownFields,
  tripletexDropdownFields,
  mappingsFor,
}) {
  const dispatch = useDispatch();

  const {
    searchInput,
    filterSelected,
    searchFilteredArray,
    isAllMappingsSelected,
    slicedMappings,
    currentlyEditing,
    selectPreferenceHubspot,
    isEditMapping,
    deletedHubspotFields,
  } = useSelector((state) => state.mapping);

  const { settingsActiveTab, isTripletexConnected } = useSelector(
    (state) => state.preference
  );

  const {
    preferencesForProducts,
    productPreference,
    preferenceForSearchParameters,
    txProductFilterPreference,
    hubspotProductFilterPreference,
    preferenceForCreatingProduct,
    defaultProductForCreating,
  } = useSelector((state) => state.product);

  const [isLoading, setIsLoading] = useState(false);

  const [searchParams] = useSearchParams();

  const mappedList =
    (mappingsFor === "product" && mapping.length > 10
      ? slicedMappings
      : mapping
    )?.map((mappedItem, index) => {
      if (
        searchFilteredArray.includes(mappedItem.id) ||
        searchInput.length === 0 ||
        !searchInput
      )
        return renderAllMappings({
          index,
          currentlyEditing,
          mappedItem,
          dispatch,
          searchInput,
          selectPreferenceHubspot,
          isEditMapping,
          deletedHubspotFields,
          mapping: mappingsFor === "product" ? slicedMappings : mapping,
          mappingsFor,
          hubspotDropdownFields,
        });
      return null;
    }) ?? [];

  useEffect(() => {
    if (mapping && hubspotDropdownFields && hubspotDropdownFields.length > 0) {
      if (settingsActiveTab === "contact") {
        dispatch(validateHubspotProperties({ mappingsFor: "customer" }));
      } else if (settingsActiveTab === "invoice") {
        dispatch(validateHubspotProperties({ mappingsFor: "order" }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapping, settingsActiveTab]);

  useEffect(() => {
    if (
      tripletexDropdownFields &&
      tripletexDropdownFields.length > 0 &&
      mapping &&
      isTripletexConnected
    ) {
      dispatch(checkUnMappedTripletexFields({ mappingsFor }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTripletexConnected, tripletexDropdownFields, mapping, mappingsFor]);

  useEffect(() => {
    if (mapping && mappingsFor) {
      dispatch(onFilterSelectedMappings({ mappingsFor }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapping, settingsActiveTab]);

  return (
    <div>
      <div className="search-bar-main-container">
        <div className="search-bar-sub-container">
          <div className="mapping-search d-flex my-3 my-md-0">
            <input
              type="search"
              placeholder="Search"
              className="input-search"
              value={searchInput}
              onChange={(e) =>
                dispatch(
                  onSearchMappings({
                    searchString: e.target.value,
                    mappingsFor,
                  })
                )
              }
            />
            <span className="my-auto">
              <FaSearch size={15} className="me-3 search-icon" />
            </span>
          </div>
          {mappingsFor !== "product" && (
            <div className="sidebar-btn my-auto">
              <SideBar dispatch={dispatch} mappingsFor={mappingsFor} />
            </div>
          )}
          <div className="sidebar-btn my-auto ms-3">
            {filterSelected[mappingsFor].length > 0 && (
              <button
                className="sidebar-cancel-button"
                onClick={() =>
                  dispatch(onDeleteSelectedMappings({ mappingsFor }))
                }
              >
                Delete selected mappings
              </button>
            )}
          </div>
        </div>
      </div>

      {searchInput.length > 1 && searchFilteredArray.length === 0 ? (
        <SearchResultsNotFound />
      ) : (
        <Table responsive className="table my-4">
          <thead className="window-header">
            <tr>
              <th className="ps-4">
                <Form.Check
                  type="checkbox"
                  id="addPDF"
                  checked={isAllMappingsSelected[mappingsFor]}
                  onChange={(e) =>
                    dispatch(
                      onSelectAllMappings({
                        isChecked: e.target.checked,
                        mappingsFor,
                      })
                    )
                  }
                />
              </th>
              <th className="window-header-heading ps-4">
                {mappingsFor === "product"
                  ? "Tripletex Products"
                  : "Tripletex Fields"}
              </th>
              <th></th>
              <th className="window-header-heading">
                {mappingsFor === "product"
                  ? "HubSpot Products"
                  : "HubSpot Fields"}
              </th>
              <th className="window-header-heading"></th>
            </tr>
          </thead>
          <tbody>{mappedList}</tbody>
        </Table>
      )}

      <div className="d-flex justify-content-end mb-4">
        {deletedHubspotFields && deletedHubspotFields.length > 0 ? (
          <OverlayTrigger
            overlay={
              <Tooltip style={{ fontSize: "12px" }}>
                Please remap the deleted HubSpot fields.
              </Tooltip>
            }
          >
            <Button className="save-changes-disabled-button">Save</Button>
          </OverlayTrigger>
        ) : (
          <Button
            onClick={() =>
              onSaveMappings({
                dispatch,
                setIsLoading,
                searchParams,
                mapping,
                mappingsFor,
                preferencesForProducts,
                productPreference,
                preferenceForSearchParameters,
                hubspotProductFilterPreference,
                txProductFilterPreference,
                preferenceForCreatingProduct,
                defaultProductForCreating,
              })
            }
            className="save-changes-button"
            disabled={isEditMapping || deletedHubspotFields.length > 0}
          >
            {isLoading ? (
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              <span>Save mappings</span>
            )}
          </Button>
        )}
      </div>

      {mappingsFor === "product" && mapping.length > 10 && (
        <Pagination mappingsFor={mappingsFor} mappings={mapping} />
      )}
    </div>
  );
}

export default RenderMappings;

/**
 * This function is used for rendering mappings in the table
 */
function renderAllMappings({
  currentlyEditing,
  mappedItem,
  dispatch,
  searchInput,
  selectPreferenceHubspot,
  isEditMapping,
  deletedHubspotFields,
  mapping,
  mappingsFor,
  hubspotDropdownFields,
}) {
  // If Editing
  if (mappedItem.id === currentlyEditing) {
    return (
      <tr key={mappedItem.id} className="add-mapping-container">
        <td></td>
        <td style={{ width: "400px" }} className="cvr-name ps-4">
          <span>{mappedItem.Tx_FieldName_HRF}</span>
        </td>
        <td>
          <img
            src={require("../../assets/images/arrowIcon.png")}
            alt="arrow-icon"
            className="arrow-icon"
            style={{
              transform: "scaleX(-1)",
            }}
          />
        </td>
        <td style={{ width: "400px" }}>
          <MappingDropdown
            mappedItem={mappedItem}
            mappings={mapping}
            hubspotDropdownFields={hubspotDropdownFields}
            mappingsFor={mappingsFor}
          />
        </td>
        <td className="p-0 d-flex flex-row justify-content-end">
          <>
            {selectPreferenceHubspot && (
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip
                    style={{ fontSize: "12px" }}
                    className="dropdown-tool-tip"
                  >
                    Add
                  </Tooltip>
                }
              >
                <button
                  onClick={() =>
                    dispatch(
                      onSaveEditedMappings({ id: mappedItem.id, mappingsFor })
                    )
                  }
                  className="add-icon-button"
                >
                  <AiOutlineCheck className="add-icon" />
                </button>
              </OverlayTrigger>
            )}
          </>
          <OverlayTrigger
            placement="bottom"
            overlay={
              <Tooltip
                style={{ fontSize: "12px" }}
                className="dropdown-tool-tip"
              >
                Cancel
              </Tooltip>
            }
          >
            <button
              onClick={() => dispatch(onCancelMapping())}
              className="delete-icon-button"
            >
              <ImCross className="delete-icon" />
            </button>
          </OverlayTrigger>
        </td>
      </tr>
    );
  }
  // Untouched Row
  else
    return (
      <tr key={mappedItem.id} className="mapping-container">
        <td className="ps-4">
          <Form.Check
            type="checkbox"
            id="addPDF"
            disabled={
              mappedItem.Tx_Field_Required ||
              (mappingsFor === "product" &&
                mappedItem.HS_Fields.length === 0) ||
              (mappingsFor !== "product" && !mappedItem.HS_FieldName_CRF)
            }
            checked={mappedItem.isChecked}
            onChange={(e) =>
              dispatch(
                onSelectMapping({
                  isChecked: e.target.checked,
                  mappingsFor,
                  fortnoxValue: mappedItem.FX_FieldName_CRF,
                })
              )
            }
          />
        </td>
        <td className="cvr-name ps-4" style={{ width: "400px" }}>
          <span>{mappedItem.Tx_FieldName_HRF}</span>
          <br />
          {mappingsFor === "product" ? (
            <span className="cvr-name-sub-text">
              {mappedItem.Tx_Field_Description ?? ""}
            </span>
          ) : (
            <span className="cvr-name-sub-text">
              {mappedItem.Tx_Field_Description ?? ""}
              {mappedItem.Tx_FieldType ? ` (${mappedItem.Tx_FieldType})` : ""}
            </span>
          )}
        </td>
        <td>
          <img
            src={require("../../assets/images/arrowIcon.png")}
            alt="arrow-icon"
            className="arrow-icon"
            style={{
              transform: "scaleX(-1)",
            }}
          />
        </td>
        {mappingsFor === "product" ? (
          <td>
            <CustomProductMappingDropdown
              mappedItem={mappedItem}
              mappings={mapping}
              hubspotDropdownFields={hubspotDropdownFields}
              mappingsFor={mappingsFor}
              deletedHubspotFields={deletedHubspotFields}
            />
          </td>
        ) : (
          <td className="cvr-name" style={{ width: "400px" }}>
            <span>
              {mappedItem.HS_FieldName_HRF === undefined ? (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip
                      style={{ fontSize: "12px" }}
                      className="dropdown-tool-tip"
                    >
                      To add mapping for this field click on Action-edit or
                      leave as it is to not map this field.
                    </Tooltip>
                  }
                >
                  <span style={{ cursor: "pointer", color: "#7a91ab" }}>
                    Not Mapped
                  </span>
                </OverlayTrigger>
              ) : (
                <>
                  <span>{mappedItem.HS_FieldName_HRF}</span>
                  <br />
                  {mappingsFor === "product" ? (
                    <span className="cvr-name-sub-text">
                      {mappedItem.HS_Field_Description ?? ""}
                    </span>
                  ) : (
                    <span className="cvr-name-sub-text">
                      {mappedItem.HS_Field_Description ?? ""}
                      {mappedItem.HS_FieldType
                        ? ` (${mappedItem.HS_FieldType})`
                        : ""}
                    </span>
                  )}
                  <br />
                  {mappedItem.HS_FieldName_CRF === undefined
                    ? ""
                    : deletedHubspotFields.includes(
                        mappedItem.HS_FieldName_CRF
                      ) && (
                        <span className="error-text">
                          (custom property deleted)
                        </span>
                      )}
                </>
              )}
            </span>
          </td>
        )}
        <td>
          <div className="d-flex flex-row justify-content-end align-items-center">
            <Dropdown
              className={"me-5 " + (isEditMapping ? "disabled-dropdown " : " ")}
            >
              <Dropdown.Toggle
                disabled={isEditMapping}
                className={"dropdown-button"}
              >
                Actions
              </Dropdown.Toggle>

              <Dropdown.Menu
                renderOnMount={true}
                popperConfig={{ strategy: "fixed" }}
              >
                {mappingsFor !== "product" && (
                  <Dropdown.Item
                    onClick={() =>
                      dispatch(
                        onEditMapping({
                          id: mappedItem.id,
                        })
                      )
                    }
                  >
                    Edit Mapping
                  </Dropdown.Item>
                )}
                <Dropdown.Item
                  disabled={
                    mappedItem.Tx_Field_Required ||
                    (mappingsFor !== "product" &&
                      mappedItem.HS_FieldName_CRF === undefined) ||
                    (mappingsFor === "product" &&
                      ((mappedItem.HS_Fields &&
                        mappedItem.HS_Fields.length === 0) ||
                        !mappedItem?.HS_Fields))
                  }
                  onClick={() => {
                    dispatch(
                      onDeleteExistingMapping({
                        id: mappedItem.id,
                        mappingsFor,
                      })
                    );
                    dispatch(
                      onSearchMappings({
                        searchString: searchInput,
                        mappingsFor,
                      })
                    );
                  }}
                >
                  Delete Mapping
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </td>
      </tr>
    );
}

/**
 * This function is used for saving the mappings
 */
export async function onSaveMappings({
  dispatch,
  setIsLoading,
  searchParams,
  mapping,
  mappingsFor,
  preferencesForProducts,
  productPreference,
  preferenceForSearchParameters,
  hubspotProductFilterPreference,
  txProductFilterPreference,
  preferenceForCreatingProduct,
  defaultProductForCreating,
}) {
  try {
    setIsLoading(true);
    const portalId = searchParams.get("portalId");
    const validMappings = [];
    mapping.forEach((mappedItem) => {
      let newMapping = {};
      if (
        mappingsFor !== "product" &&
        mappedItem.HS_FieldName_CRF !== undefined
      ) {
        newMapping = {
          Tx_FieldName_CRF: mappedItem.Tx_FieldName_CRF,
          HS_FieldName_HRF: mappedItem.HS_FieldName_HRF,
          HS_FieldName_CRF: mappedItem.HS_FieldName_CRF,
          HS_Field_Description: mappedItem.HS_Field_Description,
          Tx_Field_Description: mappedItem.Tx_Field_Description,
          Tx_FieldType: mappedItem.Tx_FieldType,
          HS_FieldType: mappedItem.HS_FieldType,
          Tx_FieldName_HRF: mappedItem.Tx_FieldName_HRF,
        };
      } else if (
        mappingsFor === "product" &&
        mappedItem.HS_Fields &&
        mappedItem.HS_Fields.length > 0
      ) {
        newMapping = {
          Tx_FieldName_CRF: mappedItem.Tx_FieldName_CRF,
          Tx_FieldName_HRF: mappedItem.Tx_FieldName_HRF,
          Tx_FieldType: mappedItem.Tx_FieldType,
          Tx_Field_Description: mappedItem.Tx_Field_Description,
          HS_Fields: mappedItem.HS_Fields,
        };
      }

      if (mappedItem.Tx_Field_Required) {
        newMapping.Tx_Field_Required = mappedItem.Tx_Field_Required;
      }

      if (Object.keys(newMapping).length > 0) {
        validMappings.push(newMapping);
      }
    });
    await axios.post(getUrl("REACT_APP_SAVE_MAPPINGS"), {
      portalId: parseInt(portalId),
      mapping: validMappings,
      mappingObject: mappingsFor,
    });

    await axios.post(getUrl("REACT_APP_SAVE_PREFERENCES"), {
      portalId: parseInt(portalId),
      preferences: {
        product: {
          generateOrder: {
            create:
              preferencesForProducts === "do not generate invoice" ||
              preferencesForProducts === "find create product"
                ? false
                : true,
            mapping:
              preferencesForProducts === "use default product" &&
              productPreference?.value
                ? {
                    txDefaultProduct: productPreference,
                  }
                : {},
          },
          createProduct: {
            customProduct: {
              useTxDefaultProduct:
                preferencesForProducts === "find create product" &&
                preferenceForCreatingProduct
                  ? true
                  : false,
              mapping:
                preferenceForCreatingProduct &&
                preferencesForProducts === "find create product" &&
                defaultProductForCreating?.value
                  ? {
                      txDefaultProduct: defaultProductForCreating,
                    }
                  : {},
            },
            create:
              preferencesForProducts === "find create product" ? true : false,
          },
          filterOptions: {
            filterTxProperty: txProductFilterPreference,
            filterHsProperty: hubspotProductFilterPreference,
          },
          searchParameters:
            preferenceForSearchParameters === "select search parameters"
              ? false
              : true,
        },
      },
      preferenceType: "product",
    });

    dispatch(checkIsMappingsSaved({ mappingsFor }));
    dispatch(
      setShowSuccessModal({
        message:
          "cmsContent.notifications.mappingWindow.successContent.saveMapping.message",
      })
    );
  } catch (error) {
    console.log(error);
    let errorMessage;
    if (error.response && error.response.data.message)
      errorMessage = error.response.data.message;
    else errorMessage = error.message;

    console.log(errorMessage);
    dispatch(setShowErrorModal({ message: errorMessage }));
  } finally {
    setIsLoading(false);
  }
}
